import React, { memo } from "react";

import {
  Box,
  Card,
  Heading,
  List,
  ListItem,
  Stack,
  Strong,
  Text,
} from "@origin-digital/ods-core";
import { IconRenew } from "@origin-digital/ods-icons";

import { DialogTrigger, UnderlinedText } from "@/components";
import { IconReduction, IconSolarEnergyGenerated } from "@/components/icons";
import { IISystemSizeMinMaxResponse } from "@/contract/generated/electrify-v1";
import { formatLabel, formatWordWithIndefiniteArticles } from "@/utils";

import { solarOutlook as content } from "../content.json";

type SummaryProps = {
  recommendationSystem: IISystemSizeMinMaxResponse;
};

export const Summary = memo(({ recommendationSystem }: SummaryProps) => {
  const { label, testId } = content;

  const { size, averageDailyGenerationAccurassi, billSavingPercent, max } =
    recommendationSystem;

  return (
    <Stack space="medium" data-id={testId.summary}>
      <Heading variant="h4" weight="bold">
        <Strong>
          {formatLabel(
            label.overviewHeading.heading,
            (value) => formatWordWithIndefiniteArticles(value.toString()),
            `${size}kW`
          )}
        </Strong>
      </Heading>

      <Box
        style={{ gap: "8px" }}
        display="flex"
        paddingX={{
          xs: "none",
          sm: "none",
          lg: "none",
        }}
      >
        <Box display="flex" marginX={["none", "none"]} width="full">
          <Card outline paddingXSpecific="none" paddingYSpecific="none">
            <Box padding={["medium", "large"]}>
              <Stack space="xsmall">
                <IconSolarEnergyGenerated color="secondary" />
                <Stack space="none">
                  <Heading variant="h4" weight="bold">
                    {`${averageDailyGenerationAccurassi} kWh`}
                  </Heading>
                  <Text variant="body-small">
                    {label.overviewHeading.generatedForYou}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>

        <Box display="flex" marginX={["none", "none"]} width="full">
          <Card outline paddingXSpecific="none" paddingYSpecific="none">
            <Box padding={["medium", "large"]}>
              <Stack space="xsmall">
                <IconReduction color="secondary" />
                <Stack space="none">
                  <Heading variant="h4" weight="bold">
                    {`${billSavingPercent}%`}
                  </Heading>
                  <Text variant="body-small">
                    {label.overviewHeading.reductionInYourBill}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>

        <Box display="flex" marginX={["none", "none"]} width="full">
          <Card outline paddingXSpecific="none" paddingYSpecific="none">
            <Box padding={["medium", "large"]}>
              <Stack space="xsmall">
                <IconRenew />
                <Stack space="none">
                  <Heading variant="h4" weight="bold">
                    {`~${max?.paybackPeriod || 0} years`}
                  </Heading>
                  <Text variant="body-small">
                    {label.overviewHeading.payFotItself}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Box>

      <Text variant="body-small">
        <DialogTrigger
          id=""
          title={<>{label.overviewHeading.disclaimers}</>}
          summary={
            <List>
              {content.label.disclaimerAssumptions.map((assumption, index) => (
                <ListItem key={index}>{assumption}</ListItem>
              ))}
            </List>
          }
        >
          <UnderlinedText>{label.overviewHeading.disclaimers}</UnderlinedText>
        </DialogTrigger>
      </Text>
    </Stack>
  );
});
